import type { Form } from '@jmvdevelop/svelte-form';
import type { ShareableContainer } from '$lib/container/shareableContainer';
import { getMessageFromError } from '$lib/utils/error';
import { fail, ok, type Result } from '$lib/utils/result.js';
import { temporizePromise } from '$lib/utils/temporizePromise.js';
import { toZonedDateTimeString } from '$lib/utils/zonedDateTime';
import { documentName } from '$gql/documentName';
import { useFragment } from '$gql/fragment-masking.js';
import {
    CreateFeedPostMutationDocument,
    NewsFeedPostFragmentDoc,
    NewsFeedQueryDocument,
    PageNewsfeed_PendingPostsCountDocument,
    PageNewsfeed_PendingPostsDocument,
    UpdateFeedPostMutationDocument,
} from '$gql/graphql.js';
import type { FeedPostFormData } from './FeedPostFormData';

type OnSubmit = (
    form: Form<FeedPostFormData, string>,
) => Promise<Result<unknown, string>>;

export const createOnSubmit: (
    container: Pick<ShareableContainer, 'apollo' | 'datalayer'>,
) => OnSubmit =
    ({ apollo, datalayer }) =>
    (form) => {
        return temporizePromise(
            500,
            (async () => {
                try {
                    const values = form.getState().values;
                    let eventStartDate =
                        values.tag === 'event' && values.startDate != null
                            ? toZonedDateTimeString(values.startDate)
                            : null;
                    let eventEndDate =
                        values.tag === 'event' && values.endDate != null
                            ? toZonedDateTimeString(values.endDate)
                            : null;
                    let pendingPublicationDate =
                        values.pendingPublicationDate != null
                            ? toZonedDateTimeString(values.pendingPublicationDate)
                            : null;

                    const result = await apollo().mutate({
                        mutation: CreateFeedPostMutationDocument,
                        variables: {
                            ...values,
                            eventEndDate,
                            eventStartDate,
                            pendingPublicationDate,
                        },
                        refetchQueries: [
                            documentName(NewsFeedQueryDocument),
                            documentName(PageNewsfeed_PendingPostsCountDocument),
                            documentName(PageNewsfeed_PendingPostsDocument),
                        ],
                    });

                    if (result.errors != null || result.data == null) {
                        return fail(getMessageFromError(result.errors));
                    }

                    let post = useFragment(
                        NewsFeedPostFragmentDoc,
                        result.data.feed.feedPost,
                    );

                    datalayer()?.push({
                        event: 'feed_post.create',
                        feed_post: {
                            id: post.id,
                            tag: post.tag,
                            hasMedias: post.medias.length > 0,
                        },
                    });

                    return ok(post.id);
                } catch (e) {
                    return fail(getMessageFromError(e));
                }
            })(),
        );
    };

export const updateOnSubmit: (
    id: number,
    container: Pick<ShareableContainer, 'apollo'>,
) => OnSubmit =
    (id, { apollo }) =>
    (form) => {
        return temporizePromise(
            500,
            (async () => {
                try {
                    const values = form.getState().values;
                    let eventStartDate =
                        values.tag === 'event' && values.startDate != null
                            ? toZonedDateTimeString(values.startDate)
                            : null;
                    let eventEndDate =
                        values.tag === 'event' && values.endDate != null
                            ? toZonedDateTimeString(values.endDate)
                            : null;

                    let pendingPublicationDate =
                        values.pendingPublicationDate != null
                            ? toZonedDateTimeString(values.pendingPublicationDate)
                            : null;

                    const result = await apollo().mutate({
                        mutation: UpdateFeedPostMutationDocument,
                        variables: {
                            ...values,
                            id,
                            eventEndDate,
                            eventStartDate,
                            pendingPublicationDate,
                        },
                        errorPolicy: 'all',
                        refetchQueries: [
                            documentName(PageNewsfeed_PendingPostsCountDocument),
                        ],
                    });
                    if (result.errors != null || result.data == null) {
                        return fail(
                            result.errors?.map((e) => e.message).join('\n') ??
                                'Erreur inconnue.',
                        );
                    }
                    let post = useFragment(
                        NewsFeedPostFragmentDoc,
                        result.data.feed.feedPost,
                    );
                    return ok(post.id);
                } catch {
                    return fail('Erreur inconnue.');
                }
            })(),
        );
    };
